/**
 * useNavDesks: the Teams the reader can see, for the sidebar's Teams section.
 *
 * The API decides visibility, and `readableDeskRows` (`sidebarTeams.ts`) is
 * the one rule for which of those Teams the reader is shown. Work reads the
 * same rule, so the two never list different Teams.
 *
 * Fetch-on-mount plus a live refresh: creating, archiving, or leaving a Team
 * dispatches a `desks:subscriptions-changed` window event, and a gate opening
 * or resolving reaches the same window events the Work feed listens to, so the
 * rail and its "waiting on you" pill update without a page reload.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { readableDeskRows } from "./sidebarTeams.js";

export interface NavDesk {
  id: string;
  name: string;
  /** Charter state — drives the status dot color. */
  state: string;
  /** Open gates, drafts and pending actions on the Team (`needsYou` on the wire). */
  openCount?: number;
  /** Open gates ADDRESSED to the reader on this Team (ADR-0044 §4). */
  needsYouForViewer?: number;
  /** User-chosen Team line icon (shared TEAM_ICON_NAMES); null/absent = derived default. */
  icon?: string | null;
}

interface DeskRow {
  id: string;
  name: string;
  state?: string;
  needsYou?: number;
  needsYouForViewer?: number;
  icon?: string | null;
  /** A personal Team: its "from" blank names exactly one person, its owner. */
  personal?: boolean;
  personalOwnerUserId?: string | null;
}

interface DesksIndexResponseShape {
  subscribed?: Array<{ desk: DeskRow }>;
  publishedByMe?: DeskRow[];
  workspaceShared?: DeskRow[];
}

function flatten(body: DesksIndexResponseShape, viewerUserId: string): NavDesk[] {
  return readableDeskRows<DeskRow>(body, viewerUserId).map((row) => ({
    id: row.id,
    name: row.name,
    state: row.state ?? "shared",
    openCount: row.needsYou,
    needsYouForViewer: row.needsYouForViewer,
    icon: row.icon ?? null,
  }));
}

/**
 * Window event dispatched after a Team is created, archived, joined, or left:
 * any change to the set of Teams the reader sees is a subscriptions change as
 * far as the rail is concerned, and this is the single refresh signal it
 * listens for.
 */
export const SUBSCRIPTIONS_CHANGED_EVENT = "desks:subscriptions-changed";

/**
 * The window events that move a gate, which the Work feed already refetches
 * on (`useWorkFeed`). The pill counts gates, so it listens to the same set.
 */
const WORK_CHANGED_EVENTS = ["work_item.updated", "tickets.invalidate", "runs.updated", "runs.created"] as const;

export function useNavDesks(viewerUserId: string): { desks: NavDesk[]; isLoading: boolean } {
  const [desks, setDesks] = useState<NavDesk[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Requests are ordered by generation, not by completion: a slow response
  // from before a mutation (archive, subscribe, rename) must never land after
  // the refetch that mutation triggered and resurrect the stale list.
  const generation = useRef(0);

  const refetch = useCallback(async (signal?: () => boolean) => {
    const gen = ++generation.current;
    try {
      const res = await fetch("/api/desks", { credentials: "include" });
      if (!res.ok) return;
      const body = (await res.json()) as DesksIndexResponseShape;
      const stale = generation.current !== gen;
      if (!stale && !signal?.()) setDesks(flatten(body, viewerUserId));
    } catch {
      // Non-fatal — the sidebar still renders its primary nav.
    } finally {
      if (generation.current === gen && !signal?.()) setIsLoading(false);
    }
  }, [viewerUserId]);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    void refetch(isCancelled);
    // Subscribing / leaving in the /desks directory refetches the rail live.
    const onChanged = () => {
      void refetch(isCancelled);
    };
    window.addEventListener(SUBSCRIPTIONS_CHANGED_EVENT, onChanged);
    for (const event of WORK_CHANGED_EVENTS) window.addEventListener(event, onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(SUBSCRIPTIONS_CHANGED_EVENT, onChanged);
      for (const event of WORK_CHANGED_EVENTS) window.removeEventListener(event, onChanged);
    };
  }, [refetch]);

  return { desks, isLoading };
}
